import type { RedoCustomerWidgetRpcClient } from "@redotech/chat-widget-sdk/customer-widget-rpc/client";
import { ConversationListItem } from "@redotech/chat-widget-sdk/customer-widget-rpc/schema/sales-agent/get-conversations-list-items";
import type { WidgetConversationResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/widget-conversation";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  sendMessageCustomerV2,
  updateCustomerCart,
} from "@redotech/redo-api-client/conversations";
import {
  getDefaultQuickResponses,
  GetResponseOutput,
  MessagingConfig,
  QuickResponse,
} from "@redotech/redo-model/concierge-conversation/rpc/concierge-conversation-schema";
import {
  ConversationMessageType,
  ConversationPlatform,
} from "@redotech/redo-model/conversation";
import { ConversationMessage } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import {
  Conversation,
  ConversationSchemaDbParser,
} from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { parseItem } from "@redotech/redo-model/parse-item";
import {
  ExtensionShopifyClientContext,
  isOnProductPage,
} from "@redotech/redo-shopify-extension/shopify";
import { useCart } from "@redotech/redo-shopify-extension/utils/cart";
import {
  addConciergeAttributionCartAttribute,
  addConciergeConversationIdToStorage,
} from "@redotech/redo-shopify-extension/utils/concierge-attribution";
import { RedoClientContext } from "@redotech/redo-web/client";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { ExtensionShopifyClient } from "@redotech/shopify-client/ajax";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Effect } from "effect";
import { useCallback, useContext } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ConversationType } from "../contexts/active-conversation";
import {
  AuthContext,
  authTokenToAuthHeader,
  ConversationAuthContext,
  SetAuthContext,
} from "../contexts/auth";
import { CurrentProductInfoQueryContext } from "../contexts/current-product-info";
import {
  EffectApiWidgetClientContext,
  type EffectApiWidgetContext,
} from "../contexts/effect-api-client";
import { GetActiveCartIdContext } from "../contexts/get-active-cart-id";
import { RedoCustomerWidgetRpcClientContext } from "../contexts/redo-customer-widget-rpc-client";
import {
  shouldUseSupportV2Api,
  SupportV2Context,
} from "../contexts/support-v2";
import { TeamInfoContext } from "../contexts/team-info";
import {
  createTempConversation,
  getConversationType,
  getNewConversationType,
  isTempConversation,
  optimisticallyAddCustomerMessage,
  TEMP_CONVERSATION_ID,
} from "../utils/add-message-utils";
import {
  getAnonymousConversationIds,
  getAnonymousConversationIdsForV2,
} from "../utils/anonymous-conversations-storage";
import { ExpandedInfoToCollect } from "../utils/chat-flows-utils";
import { isSupportV2ConversationId } from "../utils/support-v2-conversation-id";

/**
 * Whether the customer is currently on a Shopify product detail page.
 * Mirrors the Tapcart detection used by `CurrentProductInfoQueryProvider` so
 * `isPdpSource` and `currentProductInfo` agree on what counts as a PDP.
 */
function detectIsOnProductPage(): boolean {
  const onTapcart =
    !!(globalThis as unknown as { Tapcart?: unknown }).Tapcart &&
    !(globalThis as unknown as { Shopify?: unknown }).Shopify;
  return isOnProductPage(onTapcart);
}

/**
 * Convert a WidgetConversationResponse (from effect-api) to the old
 * Conversation type. The server-side adapter shapes the response to match
 * the old schema, but the widget's wire schema uses `null` for optional
 * fields while the legacy `ConversationSchemaDbParser` expects specific
 * types (no null). Strip nulls to `undefined` before parsing so the legacy
 * parser accepts the shape.
 */
export function widgetResponseToConversation(
  response: WidgetConversationResponse,
): Conversation {
  const stripNulls = <T,>(value: T | null | undefined): T | undefined =>
    value ?? undefined;
  const normalized = {
    ...response,
    assignee: stripNulls(response.assignee),
    lastCustomerResponseAt: stripNulls(response.lastCustomerResponseAt),
    inputStepType: stripNulls(response.inputStepType),
    fourDigitCodeId: stripNulls(response.fourDigitCodeId),
    messages: response.messages.map((m) => ({
      ...m,
      contentType: stripNulls(m.contentType),
      user: stripNulls(m.user),
      createdAt: stripNulls(m.createdAt),
      updatedAt: stripNulls(m.updatedAt),
      visibility: stripNulls(m.visibility),
      toolCallId: stripNulls(m.toolCallId),
      pendingToolCallId: stripNulls(m.pendingToolCallId),
      draftInfo: stripNulls(m.draftInfo),
      structuredContent: stripNulls(m.structuredContent),
      confirmationStateInformation: stripNulls(m.confirmationStateInformation),
      files: stripNulls(m.files),
    })),
  };
  return parseItem(
    normalized,
    ConversationSchemaDbParser,
    "Error parsing widget conversation response",
    response._id,
  );
}

const getResponseStatus = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }

  const { response } = error;
  if (
    typeof response !== "object" ||
    response === null ||
    !("status" in response)
  ) {
    return undefined;
  }

  return typeof response.status === "number" ? response.status : undefined;
};

const addConciergeAttributionCartAttributeBestEffort = async (
  extensionShopifyClient: ExtensionShopifyClient,
) => {
  try {
    await addConciergeAttributionCartAttribute(extensionShopifyClient);
  } catch (error) {
    // Merchant app previews and non-Shopify contexts do not expose the Ajax cart endpoint.
    if (getResponseStatus(error) === 404) {
      return;
    }

    console.error("Failed to add concierge attribution cart attribute:", error);
  }
};

// Private utility function for tracking concierge interactions
const trackConciergeInteracted = async (
  rpcClient: RedoCustomerWidgetRpcClient,
  extensionShopifyClient: ExtensionShopifyClient,
  conversationId: string | null,
  options?: { supportV2?: boolean; effectApi?: EffectApiWidgetContext },
) => {
  if (conversationId && conversationId !== TEMP_CONVERSATION_ID) {
    addConciergeConversationIdToStorage(conversationId);

    const shopifySessionId = getCookie(AllCookies.ShopifySessionId);
    const interactedCall = shopifySessionId
      ? options?.supportV2 && options.effectApi
        ? (async () => {
            const client = options.effectApi!.getClient();
            await options.effectApi!.runPromise(
              client.chatWidget
                .widgetConciergeInteracted({
                  payload: { shopifySessionId, conversationId },
                })
                .pipe(Effect.orDie),
            );
          })()
        : rpcClient.conciergeInteracted({ shopifySessionId, conversationId })
      : Promise.resolve();

    await Promise.all([
      interactedCall,
      addConciergeAttributionCartAttributeBestEffort(extensionShopifyClient),
    ]);
  }
};

// Re-export for backward compatibility

export interface ConversationListItemWithType extends ConversationListItem {
  conversationType: ConversationType;
}

export interface GetResponseOutputWithMessagesToReplace
  extends GetResponseOutput {
  messagesToReplace?: ConversationMessage[];
  config?: MessagingConfig;
}

export interface ConversationQuickResponses {
  [conversationId: string]: QuickResponse[];
}

// Helper function to map ExpandedInfoToCollect to API parameters
function getCollectedFieldParams(
  collectedInfoType?: ExpandedInfoToCollect,
  conversation?: Conversation,
) {
  const params = {
    emailCollected: false,
    fourDigitCodeId: undefined as string | undefined,
  };

  if (!collectedInfoType) {
    return params;
  }

  switch (collectedInfoType) {
    case ExpandedInfoToCollect.EMAIL:
      params.emailCollected = true;
      break;
    case ExpandedInfoToCollect.CODE_VERIFICATION:
      params.fourDigitCodeId = conversation?.fourDigitCodeId || undefined;
      break;
    // NAME and CUSTOM don't need special API parameters
  }

  return params;
}

export function useAddMessageMutation(): UseMutationResult<
  {
    result: GetResponseOutputWithMessagesToReplace;
    conversationId: string | undefined;
    message?: string;
    currentConversation: Conversation | undefined;
    shouldRefresh?: boolean;
    customerAuthToken?: string;
    conversationAuthToken?: string;
  },
  Error,
  {
    message?: string;
    conversationId?: string;
    handler?: () => Promise<{
      conversationId?: string;
      messages: ConversationMessage[];
      messagesToReplace?: ConversationMessage[];
      newConversationBeforeResponse?: Conversation;
    }>;
    shouldRefresh?: boolean;
    currentStepId?: number | null;
    collectedInfoType?: ExpandedInfoToCollect;
  },
  {
    previousConversation: Conversation | undefined;
    conversationId: string | undefined;
  }
> {
  const queryClient = useQueryClient();
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);
  const client = useRequiredContext(RedoClientContext);
  const auth = useContext(AuthContext);
  const { data: currentProductInfo } = useRequiredContext(
    CurrentProductInfoQueryContext,
  );
  const getActiveCartId = useRequiredContext(GetActiveCartIdContext);
  const extensionShopifyClient = useRequiredContext(
    ExtensionShopifyClientContext,
  );
  const teamInfo = useRequiredContext(TeamInfoContext);
  const [_, setLocalStorageConversationId] = useCurrentConversationId();
  const cart = useCart();
  const setAuth_ = useContext(SetAuthContext);
  const { setConversationAuth, getConversationAuth } = useRequiredContext(
    ConversationAuthContext,
  );
  const { setQuickResponsesForConversation } = useQuickResponses();
  const supportV2 = useContext(SupportV2Context);
  const effectApi = useContext(EffectApiWidgetClientContext);

  return useMutation({
    mutationKey: ["addMessage"],
    mutationFn: async ({
      message,
      conversationId,
      handler,
      shouldRefresh = false,
      currentStepId,
      collectedInfoType,
    }: {
      message?: string;
      conversationId?: string;
      handler?: () => Promise<{
        conversationId?: string;
        messages: ConversationMessage[];
        messagesToReplace?: ConversationMessage[];
        newConversationBeforeResponse?: Conversation;
      }>;
      shouldRefresh?: boolean;
      currentStepId?: number | null;
      collectedInfoType?: ExpandedInfoToCollect;
    }) => {
      const currentConversation = conversationId
        ? queryClient.getQueryData<Conversation>([
            "conversation",
            conversationId,
          ])
        : undefined;

      let result: GetResponseOutputWithMessagesToReplace;
      let customerAuthToken: string | undefined;
      let conversationAuthToken: string | undefined;

      if (handler) {
        const handlerResult = await handler();

        // If handler returned a new conversationId (e.g., temp -> real), use it
        if (handlerResult.conversationId) {
          conversationId = handlerResult.conversationId;
        }
        if (handlerResult.newConversationBeforeResponse) {
          conversationId = handlerResult.newConversationBeforeResponse._id;
        }

        result = {
          messages: handlerResult.messages,
          messagesToReplace: handlerResult.messagesToReplace,
          newConversationBeforeResponse:
            handlerResult.newConversationBeforeResponse ?? null,
        };
      } else {
        // Determine if this is an AI conversation or human conversation
        if (!message) {
          throw new Error("Message is required when no handler is provided");
        }
        const conversationType = getNewConversationType(
          teamInfo.conciergeLiveChat,
          currentConversation,
        );

        let initialMessages =
          currentConversation?._id === TEMP_CONVERSATION_ID
            ? currentConversation?.messages?.map((msg) => ({
                type: msg.type,
                content: msg.content,
              }))
            : undefined;
        // Because we optimistically update the conversation,
        // the last message may be the customer's own placeholder message.
        // We're about to send that message, so we need to remove it from the initial messages
        if (
          initialMessages?.at(-1)?.type === ConversationMessageType.CUSTOMER
        ) {
          initialMessages = initialMessages.slice(0, -1);
        }

        // ----- Support-V2 branch: route through effect-api -----
        if (shouldUseSupportV2Api(supportV2, effectApi)) {
          const client = effectApi.getClient();
          const collectedFieldParams = getCollectedFieldParams(
            collectedInfoType,
            currentConversation,
          );
          const supportV2ConversationId =
            currentConversation &&
            isSupportV2ConversationId(currentConversation._id)
              ? currentConversation._id
              : undefined;

          const payload = {
            message,
            customerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            currentProductInfo,
            activeCartId: await getActiveCartId(),
            isPdpSource: detectIsOnProductPage(),
            currentStepId: currentStepId ?? null,
            emailCollected: collectedFieldParams.emailCollected,
            fourDigitCodeId: collectedFieldParams.fourDigitCodeId,
            generateQuickResponses: true,
          };

          const v2Result = !supportV2ConversationId
            ? await effectApi.runPromise(
                client.chatWidget
                  .createWidgetConversation({
                    payload: { ...payload, initialMessages },
                  })
                  .pipe(Effect.orDie),
              )
            : await effectApi.runPromise(
                client.chatWidget
                  .sendWidgetMessage({
                    path: { id: supportV2ConversationId },
                    payload,
                  })
                  .pipe(Effect.orDie),
              );

          const conv = widgetResponseToConversation(v2Result.conversation);
          conversationId = conv._id;
          conversationAuthToken = v2Result.conversationToken ?? undefined;
          // For both temp and non-temp v2 paths, use `newConversationBeforeResponse`
          // to replace the cache in a single atomic update. AI responses arrive
          // asynchronously via the SSE stream, so we don't return them as `messages`.
          result = {
            messages: [],
            messagesToReplace: undefined,
            newConversationBeforeResponse: conv,
            config: {
              token: v2Result.conversationToken ?? undefined,
              conversationId: conv._id,
              teamId: conv.team,
            },
            quickResponses: v2Result.quickResponses as
              | QuickResponse[]
              | undefined,
          };
        } else if (conversationType === ConversationType.AI) {
          result = await rpcClient.getConciergeResponse(
            {
              conversationId:
                currentConversation && !isTempConversation(currentConversation)
                  ? currentConversation._id
                  : undefined,
              message,
              customerTimezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone,
              currentProductInfo,
              activeCartId: await getActiveCartId(),
              initialMessages,
              isPdpSource: detectIsOnProductPage(),
              generateQuickResponses: true,
            },
            {
              requestHeaders: auth?.token
                ? authTokenToAuthHeader(auth.token)
                : undefined,
            },
          );

          conversationAuthToken = result.config?.token;
        } else {
          const collectedFieldParams = getCollectedFieldParams(
            collectedInfoType,
            currentConversation,
          );

          const conversationSpecificToken =
            currentConversation &&
            currentConversation._id !== TEMP_CONVERSATION_ID
              ? getConversationAuth(currentConversation._id)
              : undefined;

          const sendMessageResult = await sendMessageCustomerV2(client, {
            conversationId:
              currentConversation &&
              currentConversation._id !== TEMP_CONVERSATION_ID
                ? currentConversation._id
                : undefined,
            token: conversationSpecificToken || auth?.token,
            teamId: teamInfo.id,
            message,
            welcomeMessages: initialMessages,
            currentStepId: currentStepId ?? null,
            emailCollected: collectedFieldParams.emailCollected,
            fourDigitCodeId: collectedFieldParams.fourDigitCodeId,
            isConvertedConversation: conversationType === ConversationType.LIVE,
          });

          if (
            cart?.token &&
            sendMessageResult.conversation?._id &&
            sendMessageResult.config.token
          ) {
            await updateCustomerCart(client, {
              conversationId: sendMessageResult.conversation?._id,
              cartToken: cart.token,
              token:
                conversationSpecificToken ||
                auth?.token ||
                sendMessageResult.config.token,
            });
          }

          customerAuthToken = sendMessageResult.config?.customerAuthToken;
          conversationAuthToken = sendMessageResult.config?.token;

          result = sendMessageResult;
        }
      }

      return {
        result,
        conversationId,
        message,
        currentConversation,
        shouldRefresh,
        customerAuthToken,
        conversationAuthToken,
      };
    },
    onMutate: async ({ message, conversationId }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ["conversation", conversationId],
      });

      // Snapshot the previous value
      const previousConversation = queryClient.getQueryData<Conversation>([
        "conversation",
        conversationId,
      ]);
      const conversationType = getNewConversationType(
        teamInfo.conciergeLiveChat,
        previousConversation,
      );

      if (message) {
        const optimisticConversation = optimisticallyAddCustomerMessage(
          message,
          previousConversation,
          conversationType,
          teamInfo,
        );
        queryClient.setQueryData(
          ["conversation", optimisticConversation._id],
          optimisticConversation,
        );
        if (previousConversation?._id !== optimisticConversation._id) {
          setLocalStorageConversationId(optimisticConversation._id);
        }
      }

      // Return a context object with the snapshotted value
      return { previousConversation, conversationId };
    },
    onError: (err, variables, context) => {
      if (context?.conversationId && context?.previousConversation) {
        queryClient.setQueryData(
          ["conversation", context.conversationId],
          context.previousConversation,
        );
      }
    },
    onSuccess: (
      { result, conversationId, customerAuthToken, conversationAuthToken },
      variables,
    ) => {
      let finalConversationId = conversationId;

      // if the conversation is returned use that else just set new messages
      const newConversationBeforeResponse =
        result.newConversationBeforeResponse;
      if (newConversationBeforeResponse) {
        finalConversationId = newConversationBeforeResponse._id;

        queryClient.setQueryData(
          ["conversation", finalConversationId],
          (old: Conversation | undefined) => {
            if (
              old &&
              old.messages.length >
                newConversationBeforeResponse.messages.length
            ) {
              return {
                ...newConversationBeforeResponse,
                messages: old.messages,
              };
            }

            return newConversationBeforeResponse;
          },
        );
      } else if (
        result.messages.length > 0 ||
        result.messagesToReplace?.length
      ) {
        queryClient.setQueryData(
          ["conversation", finalConversationId],
          (old: Conversation | undefined) => {
            if (!old) {
              return old;
            }

            let updatedMessages = [...old.messages];

            if (result.messagesToReplace?.length) {
              const replaceMessageIds = new Set(
                result.messagesToReplace.map((msg) => msg._id),
              );

              updatedMessages = updatedMessages.map((msg) =>
                replaceMessageIds.has(msg._id)
                  ? result.messagesToReplace!.find(
                      (replaceMsg) => replaceMsg._id === msg._id,
                    ) || msg
                  : msg,
              );
            }

            if (result.messages.length > 0) {
              updatedMessages = [...updatedMessages, ...result.messages];
            }

            return { ...old, messages: updatedMessages };
          },
        );
      }
      if (finalConversationId) {
        setLocalStorageConversationId(finalConversationId);
      }

      // Handle token setting in onSuccess
      if (customerAuthToken && auth?.token !== customerAuthToken) {
        setAuth_?.(customerAuthToken);
      }

      if (conversationAuthToken && finalConversationId) {
        setConversationAuth(finalConversationId, conversationAuthToken);
      }

      // Store quickResponses if they exist and we have a conversation ID
      if (result.quickResponses && finalConversationId) {
        setQuickResponsesForConversation(
          finalConversationId,
          result.quickResponses,
        );
      }

      // Track concierge interaction when we have a final conversation ID
      if (finalConversationId) {
        void trackConciergeInteracted(
          rpcClient,
          extensionShopifyClient,
          finalConversationId,
          {
            supportV2: shouldUseSupportV2Api(supportV2, effectApi),
            effectApi: effectApi ?? undefined,
          },
        ).catch((error) => {
          console.error("Failed to track concierge interaction:", error);
        });
      }

      // Invalidate conversations list to ensure it's refreshed
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onSettled: (data, error, variables) => {
      if (variables.shouldRefresh && variables.conversationId) {
        void queryClient.invalidateQueries({
          queryKey: ["conversation", variables.conversationId],
        });
      }
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useCallback(
    (conversation: Conversation) => {
      queryClient.setQueryData(
        ["conversation", conversation._id],
        conversation,
      );
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    [queryClient],
  );
}

export function useConversation(conversationId: string): {
  conversation: Conversation | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);
  const teamInfo = useRequiredContext(TeamInfoContext);
  const supportV2 = useContext(SupportV2Context);
  const effectApi = useContext(EffectApiWidgetClientContext);

  const {
    data: conversation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: async (): Promise<Conversation | undefined> => {
      if (!conversationId || conversationId === TEMP_CONVERSATION_ID) {
        // Check if there's already a temp conversation in the cache (for pre-scripted conversations)
        const existingTempConversation = queryClient.getQueryData<Conversation>(
          ["conversation", TEMP_CONVERSATION_ID],
        );

        if (existingTempConversation) {
          return existingTempConversation;
        }

        // Since we are using undefined for the conversation it will just return the default type for out settings
        const conversationType = getNewConversationType(
          teamInfo.conciergeLiveChat,
          undefined,
        );
        return createTempConversation([], conversationType, teamInfo);
      }

      // Use effect-api when support-v2 is enabled
      if (shouldUseSupportV2Api(supportV2, effectApi)) {
        if (!isSupportV2ConversationId(conversationId)) {
          const conversationType = getNewConversationType(
            teamInfo.conciergeLiveChat,
            undefined,
          );
          return createTempConversation([], conversationType, teamInfo);
        }

        const client = effectApi.getClient();
        const response = await effectApi.runPromise(
          client.chatWidget
            .getWidgetConversation({ path: { id: conversationId } })
            .pipe(Effect.catchAll(() => Effect.succeed(undefined))),
        );
        if (!response) {
          return undefined;
        }
        return widgetResponseToConversation(response);
      }

      const response = await rpcClient.getConversationById({
        conversationId: conversationId.toString(),
      });
      return parseItem(
        response.conversation,
        ConversationSchemaDbParser,
        "Error parsing conversation",
        conversationId,
      );
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  return { conversation, isLoading, error: error as Error | null };
}

export const useConversationItems = (): {
  conversationItems: ConversationListItemWithType[] | undefined;
  isPending: boolean;
  error: Error | null;
  refetch: () => void;
} => {
  const teamInfo = useRequiredContext(TeamInfoContext);
  const auth = useContext(AuthContext);
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);
  const supportV2 = useContext(SupportV2Context);
  const effectApi = useContext(EffectApiWidgetClientContext);

  const {
    data: conversationItems,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["conversations", teamInfo.id],
    queryFn: async (): Promise<ConversationListItemWithType[]> => {
      // Use effect-api when support-v2 is enabled
      if (shouldUseSupportV2Api(supportV2, effectApi)) {
        const client = effectApi.getClient();
        const conversationIds = !auth?.token
          ? getAnonymousConversationIdsForV2().join(",")
          : undefined;
        const v2Items = await effectApi.runPromise(
          client.chatWidget
            .getWidgetConversationListItems({ urlParams: { conversationIds } })
            .pipe(Effect.catchAll(() => Effect.succeed([]))),
        );
        return v2Items
          .map(
            (item: {
              _id: string;
              recentMessageText: string | null;
              lastMessageCreatedAt: Date | null;
              assignee: string | null;
              platform: string;
            }) => ({
              _id: item._id,
              recentMessageText: item.recentMessageText ?? "",
              lastMessageCreatedAt: item.lastMessageCreatedAt ?? new Date(),
              assignee: item.assignee ?? undefined,
              platform: item.platform ?? ConversationPlatform.REDO_CHAT,
              conversationType: getConversationType(
                (item.platform ??
                  ConversationPlatform.REDO_CHAT) as ConversationPlatform,
                item.assignee ?? undefined,
              ),
            }),
          )
          .sort(
            (a, b) =>
              new Date(b.lastMessageCreatedAt).getTime() -
              new Date(a.lastMessageCreatedAt).getTime(),
          );
      }

      let items: ConversationListItem[] = [];
      if (!auth?.token) {
        const conversationIds = getAnonymousConversationIds();

        const rpcResult = await rpcClient.getConversationsListItems({
          conversationIds,
        });
        items = items.concat(rpcResult.listItems);
      } else {
        const rpcResult = await rpcClient.getConversationsListItems(
          {},
          { requestHeaders: authTokenToAuthHeader(auth.token) },
        );
        items = items.concat(rpcResult.listItems);
      }
      return items
        .map((item) => ({
          ...item,
          conversationType: getConversationType(
            (item.platform ??
              ConversationPlatform.REDO_CHAT) as ConversationPlatform,
            item.assignee,
          ),
        }))
        .sort(
          (a, b) =>
            new Date(b.lastMessageCreatedAt).getTime() -
            new Date(a.lastMessageCreatedAt).getTime(),
        );
    },
    enabled: !!teamInfo.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return { conversationItems, isPending, error, refetch };
};

export const REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY = "redoChatWidget";
export const useCurrentConversationId = () => {
  return useLocalStorage<string>(
    REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_currentConversationId",
    TEMP_CONVERSATION_ID,
  );
};

export const usePreviousConversationId = () => {
  return useLocalStorage<string | undefined>(
    REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_previousConversationId",
    undefined,
  );
};

export const useQuickResponses = () => {
  const [quickResponses, setQuickResponses] =
    useLocalStorage<ConversationQuickResponses>(
      REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_quickResponses",
      {},
    );

  const setQuickResponsesForConversation = useCallback(
    (conversationId: string, responses: QuickResponse[]) => {
      setQuickResponses((prev) => ({ ...prev, [conversationId]: responses }));
    },
    [setQuickResponses],
  );

  const teamInfo = useRequiredContext(TeamInfoContext);

  const getQuickResponsesForConversation = useCallback(
    (conversationId: string): QuickResponse[] => {
      if (conversationId === TEMP_CONVERSATION_ID) {
        // Check if concierge is enabled and using AI mode
        if (
          !teamInfo ||
          !teamInfo.conciergeEnabled ||
          !teamInfo.conciergeLiveChat
        ) {
          return [];
        }

        const skills = teamInfo.conciergeSettings?.skills;
        if (!skills) {
          return [];
        }

        return getDefaultQuickResponses(skills);
      }
      return quickResponses[conversationId] || [];
    },
    [quickResponses, teamInfo],
  );

  const clearQuickResponsesForConversation = useCallback(
    (conversationId: string) => {
      setQuickResponses((prev) => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
    },
    [setQuickResponses],
  );

  return {
    setQuickResponsesForConversation,
    getQuickResponsesForConversation,
    clearQuickResponsesForConversation,
  };
};

export { type AnonymousConversation } from "../utils/anonymous-conversations-storage";
